/**
 * Booking Service for V2 Services
 */

import { FirestoreService } from '../db/FirestoreService';
import { Booking, BookingData, BookingStatus } from '../models/Booking.model';

export class BookingService {
  private firestore: FirestoreService;
  private readonly COLLECTION = 'bookings';

  constructor() {
    this.firestore = FirestoreService.getInstance();
  }

  async createBooking(bookingData: Partial<BookingData>): Promise<Booking> {
    const booking = new Booking(bookingData);
    const docRef = await this.firestore.addDocument(this.COLLECTION, booking.toFirestore());
    booking.id = docRef.id;
    return booking;
  }

  async getBooking(id: string): Promise<Booking | null> {
    const doc = await this.firestore.getDocument(this.COLLECTION, id);
    return doc ? Booking.fromFirestore({ id, ...doc }) : null;
  }

  async updateBooking(id: string, updates: Partial<BookingData>): Promise<void> {
    await this.firestore.updateDocument(this.COLLECTION, id, updates);
  }

  async deleteBooking(id: string): Promise<void> {
    await this.firestore.deleteDocument(this.COLLECTION, id);
  }

  async updateBookingStatus(id: string, status: BookingStatus, actor: string, notes?: string): Promise<boolean> {
    const booking = await this.getBooking(id);
    if (!booking) return false;

    if (!booking.canTransitionTo(status)) {
      throw new Error(`Cannot transition from ${booking.status} to ${status}`);
    }

    booking.addStatusEntry(status, actor, notes);
    await this.updateBooking(id, booking.toFirestore());
    return true;
  }

  async getAllBookings(limit?: number): Promise<Booking[]> {
    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [],
      { field: 'createdAt', direction: 'desc' },
      limit
    );
    return docs.map(doc => Booking.fromFirestore(doc));
  }

  async getBookingsByUser(userId: string): Promise<Booking[]> {
    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [{ field: 'userId', operator: '==', value: userId }],
      { field: 'createdAt', direction: 'desc' }
    );
    return docs.map(doc => Booking.fromFirestore(doc));
  }

  async getBookingsByStatus(status: BookingStatus): Promise<Booking[]> {
    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [{ field: 'status', operator: '==', value: status }],
      { field: 'createdAt', direction: 'desc' }
    );
    return docs.map(doc => Booking.fromFirestore(doc));
  }

  async getBookingsByRobot(robotId: string): Promise<Booking[]> {
    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [{ field: 'robotId', operator: '==', value: robotId }],
      { field: 'createdAt', direction: 'desc' }
    );
    return docs.map(doc => Booking.fromFirestore(doc));
  }

  async getBookingsByDateRange(startDate: Date, endDate: Date): Promise<Booking[]> {
    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [
        { field: 'createdAt', operator: '>=', value: startDate },
        { field: 'createdAt', operator: '<=', value: endDate }
      ],
      { field: 'createdAt', direction: 'desc' }
    );
    return docs.map(doc => Booking.fromFirestore(doc));
  }

  async getActiveBookings(): Promise<Booking[]> {
    const activeStatuses: BookingStatus[] = ['scheduled', 'in_progress'];
    const allActive: Booking[] = [];

    for (const status of activeStatuses) {
      const docs = await this.firestore.queryDocuments(
        this.COLLECTION,
        [{ field: 'status', operator: '==', value: status }]
      );
      allActive.push(...docs.map(doc => Booking.fromFirestore(doc)));
    }

    return allActive.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }

  async getRevenueByPeriod(startDate: Date, endDate: Date): Promise<number> {
    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [
        { field: 'status', operator: '==', value: 'completed' },
        { field: 'createdAt', operator: '>=', value: startDate },
        { field: 'createdAt', operator: '<=', value: endDate }
      ]
    );

    return docs.reduce((total, doc) => {
      const booking = Booking.fromFirestore(doc);
      return total + booking.amount;
    }, 0);
  }

  async assignRobot(bookingId: string, robotId: string, actor: string): Promise<void> {
    await this.firestore.updateDocument(this.COLLECTION, bookingId, { robotId });
    await this.updateBookingStatus(bookingId, 'scheduled', actor, `Robot ${robotId} assigned`);
  }

  async getBookingStats(): Promise<{
    total: number;
    active: number;
    completed: number;
    cancelled: number;
    revenue: number;
  }> {
    const allBookings = await this.getAllBookings();

    const stats = allBookings.reduce((acc, booking) => {
      acc.total++;

      switch (booking.status) {
        case 'scheduled':
        case 'in_progress':
          acc.active++;
          break;
        case 'completed':
          acc.completed++;
          acc.revenue += booking.amount;
          break;
        case 'cancelled':
          acc.cancelled++;
          break;
      }

      return acc;
    }, {
      total: 0,
      active: 0,
      completed: 0,
      cancelled: 0,
      revenue: 0
    });

    return stats;
  }

  subscribeToBookings(callback: (bookings: Booking[]) => void): () => void {
    return this.firestore.subscribeToCollection(
      this.COLLECTION,
      (docs) => {
        const bookings = docs.map(doc => Booking.fromFirestore(doc));
        callback(bookings);
      },
      [],
      { field: 'createdAt', direction: 'desc' }
    );
  }

  subscribeToBooking(id: string, callback: (booking: Booking | null) => void): () => void {
    return this.firestore.subscribeToDocument(
      this.COLLECTION,
      id,
      (doc) => {
        const booking = doc ? Booking.fromFirestore({ id, ...doc }) : null;
        callback(booking);
      }
    );
  }

  subscribeToUserBookings(userId: string, callback: (bookings: Booking[]) => void): () => void {
    return this.firestore.subscribeToCollection(
      this.COLLECTION,
      (docs) => {
        const bookings = docs.map(doc => Booking.fromFirestore(doc));
        callback(bookings);
      },
      [{ field: 'userId', operator: '==', value: userId }],
      { field: 'createdAt', direction: 'desc' }
    );
  }
}